import { Component, forwardRef, Input, input, AfterViewInit } from '@angular/core';
import { ControlValueAccessor, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface IInputType<T = string> {
  key: T;
  label: string;
}

export interface IDynamicInputValue<T = any, U = any> {
  inputType: T;
  inputValue: U;
}

@Component({
  selector: 'pds-dynamic-input',
  templateUrl: './dynamic-input.component.html',
  styleUrls: ['./dynamic-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PDSDynamicInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class PDSDynamicInputComponent implements ControlValueAccessor {
  @Input() set types(value: IInputType[]) {
    this.controls.inputType.setValue(value[0]?.key);
    this._types = value;
  }

  get types(): IInputType[] {
    return this._types;
  }

  @Input() transformValue!: (value: any, type: any) => IDynamicInputValue;
  @Input() valueType: 'text' | 'number' = 'number';

  private _types: IInputType[] = [];

  formGroup: FormGroup;
  disable = false;

  onChange!: (v: IDynamicInputValue) => void;
  onTouch!: () => void;

  constructor() {
    this.formGroup = this.initFormGroup();
  }

  writeValue(obj: IDynamicInputValue): void {
    this.formGroup.patchValue(obj);
  }

  registerOnChange(fn: (v: IDynamicInputValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouch = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disable = isDisabled;
    isDisabled ? this.formGroup.disable() : this.formGroup.enable();
  }

  private initFormGroup() {
    return new FormGroup({
      inputType: new FormControl(''),
      inputValue: new FormControl(''),
    });
  }

  get values(): IDynamicInputValue {
    return {
      inputType: this.formGroup.get('inputType')?.value as any,
      inputValue: this.formGroup.get('inputValue')?.value as string,
    };
  }

  get controls() {
    return {
      inputType: this.formGroup.get('inputType') as FormControl,
      inputValue: this.formGroup.get('inputValue') as FormControl,
    };
  }

  onChangeType() {
    const type = this.values.inputType;
    const value = this.values.inputValue;
    this.formGroup.setValue(this.transformValue(value, type));
    this.onChange(this.values);
  }
}
